// Every tenant capability key the platform knows, with what it does — the
// admin app's tenant page renders a switch per entry. tenants.capabilities is
// a free-form JSONB (mig 235), so without this list an operator edits keys by
// NAME and has to know what each one gates; the old admin repo's editor was
// exactly that.
//
// ⚠️ capabilityCatalog.test.ts scans src/ and the migrations for every key
// the code READS (useTenantCapability('x'), capabilities.x, capabilities ->> 'x')
// and fails when one is missing here. A new flag cannot ship without a line
// saying what it does.
//
// Effective value = plan_tiers.default_capabilities ‖ tenants.capabilities
// (tenant overrides win) — get_effective_capabilities(), mig 469.

export interface CapabilityDef {
  key:   string
  label: string
  /** One sentence, in the operator's words: what turning it on does. */
  what:  string
  /** True when nothing in the app reads it today: it is recorded on the plan
   *  (plan_tiers.default_capabilities) and changes nothing yet. Shown as such,
   *  never as a working switch. */
  recordOnly?: boolean
}

export const CAPABILITIES: CapabilityDef[] = [
  { key: 'custom_roles',            label: 'Custom roles',
    what: 'Admins can create their own roles with a chosen set of permissions, and give them to members.' },
  { key: 'advisor_phi_spray',       label: 'Harvest-interval check on spray plans',
    what: 'While a spray is planned or captured, warns when the mix\'s withholding period would run past the block\'s next harvest.' },
  { key: 'advisor_phi_harvest',     label: 'Harvest-interval check on harvests',
    what: 'While picks are captured, warns when the pick date falls inside a spray\'s withholding period on that block.' },
  { key: 'advisor_qc_spc',          label: 'QC trend signal',
    what: 'Flags statistical drift on the QC dashboard\'s trend chart (control limits and run rules). Needs a season of history.' },
  { key: 'advisor_qc_defect_trend', label: 'QC defect reference band',
    what: 'While a QC value is captured, shows the usual range from the last ~20 answers for the same item and variety.' },
  { key: 'agent_app',               label: 'Agent app',
    what: 'Lets an agent business use the agent app. Set when an agent tenant is created.' },
  { key: 'mgp_advances',            label: 'Advances (pilot)',
    what: 'Shows the Advances tab under Financial: the register of advances paid against pallets. It only shows the tab; who can see the figures is decided by their financial access.' },
  { key: 'intake_weight',           label: 'Weight at intake',
    what: 'Adds a gross weight and a pallet tare to the delivery intake form; the app works out the net from the crate type\'s tare. Shows on the delivery and its sticker.' },
  { key: 'delivery_qc_later',       label: 'QC deliveries later',
    what: 'The delivery intake form no longer asks for a QC template or opens the check after a save. QC checks each pallet (or delivery) later, from the QC To check list or the delivery page.' },
  { key: 'ppecb_export',            label: 'PPECB export (plan entitlement)', recordOnly: true,
    what: 'Recorded on the plan for billing. Nothing in the app reads it yet.' },
  { key: 'farm_app',                label: 'Farm app (plan entitlement)', recordOnly: true,
    what: 'Recorded on the plan for billing. Nothing in the app reads it yet.' },
  { key: 'support_priority',        label: 'Support priority (plan entitlement)', recordOnly: true,
    what: 'Recorded on the plan (BASIC / STANDARD / HIGH). Nothing in the app reads it yet.' },
]

export const CAPABILITY_KEYS = new Set(CAPABILITIES.map(c => c.key))

/** The same truthiness useTenantCapability applies: undefined, null, false,
 *  0 and '' are off; anything else is on. */
export function capabilityOn(v: unknown): boolean {
  return v !== undefined && v !== null && v !== false && v !== 0 && v !== ''
}

export type CapabilitySetting = 'plan' | 'on' | 'off'

/** What the tenant's own row says for a key: nothing (the plan decides), or
 *  an override. */
export function settingOf(own: Record<string, unknown>, key: string): CapabilitySetting {
  if (!(key in own)) return 'plan'
  return capabilityOn(own[key]) ? 'on' : 'off'
}

/** The tenant's capabilities with one key set to a setting — 'plan' removes
 *  the override so the plan default applies again. */
export function withSetting(own: Record<string, unknown>, key: string, s: CapabilitySetting): Record<string, unknown> {
  const next = { ...own }
  if (s === 'plan') delete next[key]
  else next[key] = s === 'on'
  return next
}
